import React, { useState } from 'react';
import { 
  HelpCircle, 
  Users, 
  UserCheck, 
  GraduationCap,
  BookOpen,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Download,
  Eye,
  Settings,
  Clock,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';

const Help = () => {
  const { isAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');

  const sections = {
    overview: 'System Overview',
    registration: 'Teacher Registration',
    profile: 'Profile Setup',
    smartbuilder: 'Smart Builder Guide',
    timetables: 'Timetables Guide',
    admin: 'Admin Functions',
    troubleshooting: 'Troubleshooting'
  };

  const OverviewSection = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <HelpCircle className="w-5 h-5 mr-2 text-blue-600" />
            Welcome to Smart Manager
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            Smart Manager helps you manage academic timetables, course schedules, payroll, and administrative tasks with intelligent automation and comprehensive management features.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">For Teachers</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Register and create your profile</li>
                <li>• Manage your subjects and assignments</li>
                <li>• Create course schedules with Smart Builder</li>
                <li>• View your personal timetables</li>
              </ul>
            </div>
            
            {isAdmin() && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900 mb-2">For Administrators</h4>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>• Approve teacher registrations</li>
                  <li>• Manage all system data</li>
                  <li>• Monitor timetable creation</li>
                  <li>• Access comprehensive reports</li>
                </ul>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const RegistrationSection = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserCheck className="w-5 h-5 mr-2 text-green-600" />
            Teacher Registration Process
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Teacher Self-Registration</h4>
                <p className="text-gray-600 text-sm mt-1">
                  Go to the login page and click "Register as Teacher". Fill in your name, institutional email, and create a password.
                </p>
                <Badge variant="outline" className="mt-2">Takes 2-3 minutes</Badge>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-semibold">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Admin Approval</h4>
                <p className="text-gray-600 text-sm mt-1">
                  {isAdmin() 
                    ? "Go to Admin Panel to review and approve pending teacher registrations." 
                    : "Wait for an administrator to approve your registration. You'll receive confirmation when approved."
                  }
                </p>
                <Badge variant="outline" className="mt-2">Usually within 24 hours</Badge>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-semibold">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Profile Setup</h4>
                <p className="text-gray-600 text-sm mt-1">
                  After approval, login and go to "Teachers" to create your profile. Assign subjects you can teach.
                </p>
                <Badge variant="outline" className="mt-2">5-10 minutes</Badge>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-800">Important Notes</h4>
                <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                  <li>• Use your institutional email address for registration</li>
                  <li>• You cannot login until an admin approves your account</li>
                  <li>• Your email cannot be changed after registration</li>
                  <li>• Contact your admin if approval takes longer than expected</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const ProfileSection = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <GraduationCap className="w-5 h-5 mr-2 text-purple-600" />
            Teacher Profile Setup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Profile Information</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <strong>Name:</strong> Update if needed
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-blue-500 mr-2" />
                  <strong>Email:</strong> Auto-filled (read-only)
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                  <strong>Phone:</strong> Optional contact number
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-purple-500 mr-2" />
                  <strong>Subjects:</strong> Critical for Smart Builder
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900">Subject Assignment</h4>
              <div className="space-y-2">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <strong className="text-blue-900">Streams Available:</strong>
                  <div className="text-sm text-blue-800 mt-1">
                    • Interior Design<br/>
                    • Fashion Design<br/>
                    • MBA Design Management<br/>
                    • Skill Enhancement
                  </div>
                </div>
                
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <strong className="text-green-900">Best Practice:</strong>
                  <div className="text-sm text-green-800 mt-1">
                    Use stream and semester filters to find your subjects easily
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const SmartBuilderSection = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-indigo-600" />
            Smart Timetable Builder Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 mb-2">Teacher Mode Features</h4>
            <ul className="text-sm text-indigo-800 space-y-1">
              <li>• Only see subjects assigned to your profile</li>
              <li>• Cannot select other faculty members</li>
              <li>• Automatically assigned as faculty for your courses</li>
              <li>• Create recurring session schedules</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900">Step-by-Step Process</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                  <span>Select appropriate batch</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">2</div>
                  <span>Choose your assigned subject</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">3</div>
                  <span>Faculty auto-assigned to you</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">4</div>
                  <span>Select classroom and timing</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-xs font-bold">5</div>
                  <span>Configure schedule details</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900">Automatic Features</h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Conflict detection with existing schedules</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Holiday exclusion from session dates</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Recurring session generation</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  <span>Integration with timetables</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const TimetablesSection = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="w-5 h-5 mr-2 text-emerald-600" />
            Timetables Guide
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">By Batch</h4>
              <p className="text-sm text-blue-800">
                View complete timetable for a specific batch including all subjects and teachers.
              </p>
            </div>
            
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-semibold text-purple-900 mb-2">By Teacher</h4>
              <p className="text-sm text-purple-800">
                View your personal schedule showing all classes you're assigned to teach.
              </p>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">By Classroom</h4>
              <p className="text-sm text-green-800">
                Check classroom utilization and see what classes are scheduled in each room.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900">Features Available</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <Eye className="w-4 h-4 text-blue-500 mr-2" />
                  Weekly view with navigation
                </li>
                <li className="flex items-center">
                  <Download className="w-4 h-4 text-green-500 mr-2" />
                  Export to PDF or Excel
                </li>
                <li className="flex items-center">
                  <Settings className="w-4 h-4 text-purple-500 mr-2" />
                  Multiple filter options
                </li>
              </ul>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mr-2" />
                  Real-time updates
                </li>
                <li className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-orange-500 mr-2" />
                  Print-friendly format
                </li>
                <li className="flex items-center">
                  <Users className="w-4 h-4 text-indigo-500 mr-2" />
                  Role-based access
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const AdminSection = () => (
    isAdmin() ? (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Settings className="w-5 h-5 mr-2 text-red-600" />
              Administrator Functions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Daily Tasks</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start space-x-2">
                    <ArrowRight className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>Check Admin Panel for pending registrations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <ArrowRight className="w-4 h-4 text-green-500 mt-0.5" />
                    <span>Approve legitimate teacher registrations</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <ArrowRight className="w-4 h-4 text-purple-500 mt-0.5" />
                    <span>Monitor system usage and teacher activity</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <ArrowRight className="w-4 h-4 text-orange-500 mt-0.5" />
                    <span>Manage subjects, batches, and classrooms</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Admin Login</h4>
                <div className="p-3 bg-red-50 border border-red-200 rounded font-mono text-sm">
                  <div><strong>Email:</strong> director@nifpcmc.com</div>
                  <div><strong>Password:</strong> Director@Time</div>
                </div>
                
                <div className="space-y-2">
                  <h5 className="font-medium text-gray-800">Quick Access</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Admin Panel → Approve teachers</li>
                    <li>• Teachers → Manage profiles</li>
                    <li>• Dashboard → System overview</li>
                    <li>• Session Management → Monitor schedules</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2">Registration Workflow</h4>
              <div className="text-sm text-amber-700">
                <strong>1. Teacher Registration</strong> → <strong>2. Admin Review</strong> → <strong>3. Approval</strong> → <strong>4. Profile Creation</strong> → <strong>5. Smart Builder Access</strong>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    ) : (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Admin Access Required</h3>
          <p className="text-gray-600">
            This section contains administrator-specific information and is only visible to admin users.
          </p>
        </CardContent>
      </Card>
    )
  );

  const TroubleshootingSection = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-orange-600" />
            Common Issues & Solutions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-6">
            <div className="border-l-4 border-red-500 pl-4">
              <h4 className="font-semibold text-red-800">Can't Login After Registration</h4>
              <p className="text-sm text-red-700 mt-1">
                <strong>Solution:</strong> Your account needs admin approval before you can login. Contact your administrator or wait for approval notification.
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="font-semibold text-orange-800">No Subjects Available in Smart Builder</h4>
              <p className="text-sm text-orange-700 mt-1">
                <strong>Solution:</strong> Ensure you've created your teacher profile with subject assignments. Only subjects assigned to you will appear.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h4 className="font-semibold text-yellow-800">Profile Not Found Error</h4>
              <p className="text-sm text-yellow-700 mt-1">
                <strong>Solution:</strong> Go to Teachers section and click "Create My Profile" to set up your teacher profile with subject assignments.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-blue-800">Email Field is Read-Only</h4>
              <p className="text-sm text-blue-700 mt-1">
                <strong>Solution:</strong> This is by design. Your email is tied to your login account and cannot be changed in the profile section.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-semibold text-green-800">Can't See Other Teachers' Subjects</h4>
              <p className="text-sm text-green-700 mt-1">
                <strong>Solution:</strong> This is correct behavior. Teachers can only see and schedule their own assigned subjects for security reasons.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Need More Help?</h4>
            <p className="text-sm text-gray-600">
              Contact your system administrator or IT support team for technical issues not covered in this guide.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSection = () => {
    switch (activeSection) {
      case 'overview': return <OverviewSection />;
      case 'registration': return <RegistrationSection />;
      case 'profile': return <ProfileSection />;
      case 'smartbuilder': return <SmartBuilderSection />;
      case 'timetables': return <TimetablesSection />;
      case 'admin': return <AdminSection />;
      case 'troubleshooting': return <TroubleshootingSection />;
      default: return <OverviewSection />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <HelpCircle className="w-8 h-8 mr-3 text-blue-600" />
            Help & Instructions
          </h1>
          <p className="text-gray-600">
            Complete guide to using Smart Manager
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Navigation Sidebar */}
          <div className="lg:w-64 flex-shrink-0">
            <Card>
              <CardContent className="p-4">
                <nav className="space-y-2">
                  {Object.entries(sections).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setActiveSection(key)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeSection === key
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        {label}
                        {activeSection === key && <ChevronRight className="w-4 h-4" />}
                      </div>
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            {renderSection()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Help;